import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Download, FileText, Image, File } from "lucide-react";

// Placeholder document type until document system is implemented
export interface Document {
    id: string;
    name: string;
    type: "lease" | "receipt" | "notice" | "other";
    fileType: "pdf" | "image" | "doc" | "other";
    size?: number;
    uploadedAt: string;
    url?: string;
}

interface DocumentListProps {
    documents: Document[];
    emptyMessage?: string;
    onDownload?: (document: Document) => void;
}

export function DocumentList({
    documents,
    emptyMessage = "No documents available",
    onDownload,
}: DocumentListProps) {
    const getDocumentIcon = (fileType: Document["fileType"]) => {
        switch (fileType) {
            case "pdf":
                return <FileText className="h-5 w-5 text-red-500" />;
            case "image":
                return <Image className="h-5 w-5 text-blue-500" />;
            case "doc":
                return <FileText className="h-5 w-5 text-blue-600" />;
            default:
                return <File className="h-5 w-5 text-gray-500" />;
        }
    };

    const getTypeBadge = (type: Document["type"]) => {
        switch (type) {
            case "lease":
                return <Badge className="bg-blue-500">Lease</Badge>;
            case "receipt":
                return <Badge className="bg-green-500">Receipt</Badge>;
            case "notice":
                return <Badge className="bg-yellow-500">Notice</Badge>;
            default:
                return <Badge variant="secondary">Other</Badge>;
        }
    };

    const formatFileSize = (bytes?: number) => {
        if (!bytes) return "";
        const kb = bytes / 1024;
        if (kb < 1024) return `${kb.toFixed(1)} KB`;
        return `${(kb / 1024).toFixed(1)} MB`;
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString("en-NG", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle>Documents</CardTitle>
                <CardDescription>
                    Access your lease agreement, receipts, and other important documents
                </CardDescription>
            </CardHeader>
            <CardContent>
                {documents.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                        {emptyMessage}
                    </div>
                ) : (
                    <div className="space-y-3">
                        {documents.map((document) => (
                            <div
                                key={document.id}
                                className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                            >
                                <div className="flex items-center gap-4 flex-1 min-w-0">
                                    {/* Icon */}
                                    <div className="shrink-0">{getDocumentIcon(document.fileType)}</div>

                                    {/* Document Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <h4 className="font-medium truncate">{document.name}</h4>
                                            {getTypeBadge(document.type)}
                                        </div>
                                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                                            <span>{formatDate(document.uploadedAt)}</span>
                                            {document.size && (
                                                <>
                                                    <span>•</span>
                                                    <span>{formatFileSize(document.size)}</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Actions */}
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => onDownload?.(document)}
                                    className="shrink-0"
                                >
                                    <Download className="h-4 w-4 mr-2" />
                                    Download
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
